import React, {
  createContext,
  useEffect,
  useCallback,
  useReducer
} from 'react';
import { Auth0Client } from '@auth0/auth0-spa-js';
import jwt_decode from 'jwt-decode';
import { datadogRum } from '@datadog/browser-rum';
import SplashScreen from 'src/components/SplashScreen';
import { extractApiErrorMessage } from 'src/utils/apiErrorUtils';
import { auth0Config, auth0LoginRedirection, hubConfig } from 'src/config';
import GetLoggedInUser from '../api/GetLoggedInUser';
import { useUnleashContext } from '@unleash/proxy-client-react';

let auth0Client = null;
let isRedirectingToLogin = false; // Flag to prevent multiple login redirects
let lastLoginTimestamp = null; // Track when user last logged in to prevent false SSO logout detection
let memoryTokenCache = null; // In-memory token cache for incognito mode when localStorage fails

const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  user: null,
  loggedInUser: null
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, user, loggedInUser } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        user,
        loggedInUser
      };
    }
    case 'LOGIN': {
      const { user, loggedInUser } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user,
        loggedInUser
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loggedInUser: null
      };
    }
    case 'UPDATE_USER_INFO': {
      const { loggedInUser } = action.payload;
      return {
        ...state,
        loggedInUser
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  method: 'Auth0',
  loginWithPopup: () => Promise.resolve(),
  loginWithRedirect: () => Promise.resolve(),
  afterLoginRedirection: () => Promise.resolve(),
  getAccessToken: () => Promise.resolve(),
  logout: () => { }
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const updateUnleashContext = useUnleashContext();

  /**
   * Gets access token for querying secured endpoints
   * @param {boolean} forceRefresh - Force check Auth0 session on server (slower but detects SSO logout)
   */
  const getAccessToken = async (forceRefresh = false) => {
    try {
      // Check if auth0Client is initialized
      if (!auth0Client) {
        return { tokenAvailable: false, errorText: 'Auth0 client not initialized' };
      }

      // Always try to get token from cache first
      let accessToken;
      let cachedToken = null;

      let hadCachedToken = false;
      try {
        cachedToken = await auth0Client.getTokenSilently({ cacheMode: 'on' });
        hadCachedToken = true;

        // Store in memory cache for incognito mode fallback
        memoryTokenCache = cachedToken;

        const decodedToken = jwt_decode(cachedToken);
        const tokenExp = decodedToken.exp * 1000;
        const isTokenExpired = Date.now() >= tokenExp;
        const isTokenExpiringSoon = Date.now() >= (tokenExp - 300000);

        // If token is not expired and not expiring, check if we need force refresh
        if (!isTokenExpired && !isTokenExpiringSoon) {
          // No force refresh needed, use cached token
          if (!forceRefresh) {
            const currentRoles = decodedToken?.userRoles;
            if (!currentRoles || !Array.isArray(currentRoles) || !currentRoles.includes('Pool')) {
              console.warn('Pool role missing, redirecting to 403...');
              sessionStorage.removeItem('userRoles');
              sessionStorage.removeItem('sessionId');
              sessionStorage.removeItem('userEmail');
              if (window.location.pathname !== '/403') {
                window.location.href = '/403';
              }
              return { tokenAvailable: false, errorText: 'Pool role not found' };
            }

            sessionStorage.setItem('userRoles', JSON.stringify(currentRoles));
            return { tokenAvailable: true, accessToken: cachedToken };
          }

          // Force refresh requested - try server check for SSO detection
          try {
            accessToken = await auth0Client.getTokenSilently({ cacheMode: 'off' });
          } catch (refreshError) {
            // Force refresh failed - this could be:
            // 1. Real SSO logout in normal mode (should trigger logout redirect)
            // 2. Incognito mode where iframe doesn't work (should fall back to cached token)

            // Only fall back to cached token if we're within grace period (likely incognito)
            const withinGracePeriod = lastLoginTimestamp && (Date.now() - lastLoginTimestamp) < 3000;

            if (cachedToken && withinGracePeriod) {
              // Within grace period - likely incognito mode, use cached token
              accessToken = cachedToken;
            } else {
              // Outside grace period - likely real SSO logout, throw error to trigger logout
              throw refreshError;
            }
          }
        } else {
          // Token expired or expiring - must refresh
          accessToken = await auth0Client.getTokenSilently({ cacheMode: 'off' });
        }
      } catch (cacheError) {
        // No cached token from Auth0
        // IMPORTANT: If this error came from force refresh failure (hadCachedToken=true),
        // don't use memory cache - let it propagate to trigger SSO detection
        if (hadCachedToken) {
          // Error came from force refresh failure, not from missing cache
          // Don't use memory cache - throw to trigger SSO detection
          throw cacheError;
        }

        // No cached token from Auth0
        // In incognito mode within grace period, use memory cache
        // Outside grace period, we need to try server refresh to detect SSO logout
        const withinGracePeriod = lastLoginTimestamp && (Date.now() - lastLoginTimestamp) < 3000;

        if (memoryTokenCache && withinGracePeriod) {
          // Within grace period - likely incognito mode startup, use memory cache
          const decodedMemToken = jwt_decode(memoryTokenCache);
          const memTokenExp = decodedMemToken.exp * 1000;
          const isMemTokenExpired = Date.now() >= memTokenExp;

          if (!isMemTokenExpired) {
            accessToken = memoryTokenCache;
          } else {
            // Memory token expired, try fresh token
            accessToken = await auth0Client.getTokenSilently({ cacheMode: 'off' });
            memoryTokenCache = accessToken;
          }
        } else {
          // Outside grace period or no memory cache - try fresh token from server
          // This will throw if there's an SSO logout
          try {
            accessToken = await auth0Client.getTokenSilently({ cacheMode: 'off' });
            memoryTokenCache = accessToken;
          } catch (freshError) {
            // Server refresh failed - check if we have valid memory token as last resort
            if (memoryTokenCache) {
              const decodedMemToken = jwt_decode(memoryTokenCache);
              const memTokenExp = decodedMemToken.exp * 1000;
              const isMemTokenExpired = Date.now() >= memTokenExp;

              if (!isMemTokenExpired) {
                // Have valid memory token - this is incognito mode, use it
                accessToken = memoryTokenCache;
              } else {
                // Memory token also expired - must throw
                throw freshError;
              }
            } else {
              throw freshError;
            }
          }
        }
      }

      // Decode token to check user roles (if we didn't already in cache check)
      const finalDecodedToken = jwt_decode(accessToken);
      const finalRoles = finalDecodedToken?.userRoles;

      if (!finalRoles || !Array.isArray(finalRoles) || !finalRoles.includes('Pool')) {
        console.warn('Pool role missing, redirecting to 403...');
        sessionStorage.removeItem('userRoles');
        sessionStorage.removeItem('sessionId');
        sessionStorage.removeItem('userEmail');
        if (window.location.pathname !== '/403') {
          window.location.href = '/403';
        }
        return { tokenAvailable: false, errorText: 'Pool role not found' };
      }

      sessionStorage.setItem('userRoles', JSON.stringify(finalRoles));

      return { tokenAvailable: true, accessToken: accessToken };
    } catch(error) {
      console.log('Error getting access token: ', error.message);

      // SSO logout detection: Only redirect if user WAS authenticated and session expired
      // Don't redirect on initial load (incognito mode, new users) - let AuthGuard handle that
      // Don't redirect if we're processing an Auth0 callback (has code/state params)
      // Don't redirect within 3 seconds of login (prevents false detection in incognito mode)
      const urlParams = new URLSearchParams(window.location.search);
      const isProcessingCallback = urlParams.has('code') && urlParams.has('state');
      const recentlyLoggedIn = lastLoginTimestamp && (Date.now() - lastLoginTimestamp) < 3000; // 3 seconds grace period

      // Only trigger SSO logout if:
      // 1. User was authenticated
      // 2. Not processing callback
      // 3. Not recently logged in (within 3 seconds)
      // 4. NOT caused by incognito mode where token simply can't be refreshed
      const likelyIncognitoMode = recentlyLoggedIn; // If recently logged in and getting login_required, likely incognito

      // NOTE: consent_required is intentionally NOT treated as an SSO logout.
      // On a non-verifiable (HTTP) callback like http://localhost:3000, a forced
      // silent renewal can't show the Auth0 consent prompt and returns consent_required
      // even though the cached session is still valid. Only login_required means the
      // SSO session actually expired.
      if (error.error === 'login_required' &&
          state.isAuthenticated &&
          !isProcessingCallback &&
          !likelyIncognitoMode) {
        console.warn('SSO logout detected - session expired while user was authenticated');

        // Prevent multiple simultaneous redirects
        if (isRedirectingToLogin) {
          return { tokenAvailable: false, errorText: 'Session expired - redirect in progress' };
        }

        isRedirectingToLogin = true;

        // Keep email in sessionStorage for SSO logout - it will be read by AuthGuard -> LoginView
        // Set a flag to indicate this is an SSO logout scenario
        sessionStorage.setItem('ssoLogout', 'true');
        sessionStorage.removeItem('userRoles');
        sessionStorage.removeItem('sessionId');
        sessionStorage.removeItem('groupId');

        dispatch({
          type: 'LOGOUT'
        });

        // Logout and redirect to login page
        auth0Client.logout({
          logoutParams: {
            returnTo: window.location.origin + '/login',
          },
        });

        return { tokenAvailable: false, errorText: 'Session expired' };
      }

      return { tokenAvailable: false, errorText: extractApiErrorMessage(error) };
    }
  };

  /**
   * Handles redirection after logging in via Auth0 universal login (basically, takes care of the flow after a user logs in successfully)
   * @param {*} options represents the payload passed w.r.t redirection by the caller
   */
  const afterLoginRedirection = async (options) => {
    await auth0Client.handleRedirectCallback()

    // Try checkSession but don't fail if it doesn't work (incognito mode)
    try {
      await auth0Client.checkSession()
    } catch (e) {
      console.log('checkSession failed (likely incognito mode):', e.message);
    }

    const isAuthenticated = await auth0Client.isAuthenticated();
    if (isAuthenticated) {
      const user = await auth0Client.getUser();

      // Store user email in sessionStorage for SSO logout scenarios
      if (user?.email) {
        sessionStorage.setItem('userEmail', user.email);
      }

      const {tokenAvailable, accessToken} = await getAccessToken()
      if (!tokenAvailable) {
        console.error('Error getting access token:', accessToken);
        return;
      }

      //get the user info from the backend endpoint
      const loggedInUser = await GetLoggedInUser(accessToken)
      // if this gets a user not found error then redirect to hub
      if (
        loggedInUser.success === false &&
        loggedInUser.status === 404 &&
        loggedInUser.error === 'User not found with this authentication token'
      ) {
        window.location.href = hubConfig.hubURL;
      }

      // Guard against a failed token/user fetch
      if (!loggedInUser?.userInfo?.user?.groups) {
        console.error('afterLoginRedirection: no user info returned, aborting redirect setup');
        return;
      }

      const defaultGroup = loggedInUser.userInfo.user.groups.sort((a, b) => a.group.id - b.group.id)[0].group;

      if (!sessionStorage.getItem('groupId')) {
        sessionStorage.setItem('groupId', defaultGroup.id)
        updateUnleashContext({ userId: defaultGroup.id });
      } else {
        const groupId = sessionStorage.getItem('groupId');
        if (!!groupId) {
          updateUnleashContext({ userId: groupId });
        }
      }

      // Set login timestamp to prevent false SSO logout detection immediately after login
      lastLoginTimestamp = Date.now();

      dispatch({
        type: 'LOGIN',
        payload: {
          user: {
            id: user.sub,
            avatar: user.picture,
            email: user.email,
            name: user.name,
            tier: 'Premium'
          },
          loggedInUser: {
            id: loggedInUser.userInfo.user.id,
            emailAddress: loggedInUser.userInfo.user.emailAddress,
            fullName: loggedInUser.userInfo.user.fullName,
            preferredName: loggedInUser.userInfo.user.preferredName,
            adminRole: loggedInUser.userInfo.user.adminRole,
            groupName: defaultGroup.name,
            groups: loggedInUser.userInfo.user.groups,
            isApprover: loggedInUser.userInfo.isApprover,
            createdAt: loggedInUser.userInfo.user.createdAt
          }
        }
      });

      datadogRum.setUser({
        id: loggedInUser.userInfo.user.id.toString(),
        name: loggedInUser.userInfo.user.fullName,
        email: loggedInUser.userInfo.user.emailAddress,
      });
      datadogRum.setGlobalContextProperty('groupName', defaultGroup.name);
    }
  }


  const loginWithRedirect = async(options) => {
    //here the await does not actually await because it transfers to a different page for fetching credentials, hence the afterLoginRedirect method
    await auth0Client.loginWithRedirect({
        ...auth0LoginRedirection,
        ...options,
        authorizationParams: {
          ...auth0LoginRedirection.authorizationParams,
          ...options?.authorizationParams,
        }
    });
  }


  const loginWithPopup = async (options) => {
    //here the await actually awaits because it stays in the same page while the popup prompt is answered
    await auth0Client.loginWithPopup(options);

    const isAuthenticated = await auth0Client.isAuthenticated();

    if (isAuthenticated) {
      const user = await auth0Client.getUser();

      dispatch({
        type: 'LOGIN',
        payload: {
          user: {
            id: user.sub,
            avatar: user.picture,
            email: user.email,
            name: user.name,
            tier: 'Premium'
          }
        }
      });
    }
  };

  const refreshLoggedInUser = async () => {
    const { tokenAvailable, accessToken } = await getAccessToken();
    if (!tokenAvailable) return;
    const result = await GetLoggedInUser(accessToken);
    if (!result.success) return;
    const { user: userData } = result.userInfo;
    if (!userData.groups?.length) return;
    const defaultGroup = userData.groups.sort((a, b) => a.group.id - b.group.id)[0].group;
    dispatch({
      type: 'UPDATE_USER_INFO',
      payload: {
        loggedInUser: {
          id: userData.id,
          emailAddress: userData.emailAddress,
          fullName: userData.fullName,
          preferredName: userData.preferredName,
          adminRole: userData.adminRole,
          groupName: defaultGroup.name,
          groups: userData.groups,
          isApprover: result.userInfo.isApprover,
          createdAt: userData.createdAt
        }
      }
    });
  };

  const logout = async () => {
    // Clear stored roles, email, and session data
    sessionStorage.removeItem('userRoles');
    sessionStorage.removeItem('userEmail');
    sessionStorage.removeItem('sessionId');
    sessionStorage.removeItem('groupId');

    datadogRum.clearUser();
    datadogRum.removeGlobalContextProperty('groupName');

    auth0Client.logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });

    dispatch({
      type: 'LOGOUT'
    });
  };

  /**
   * initializes the auth context after it is loaded
   */
  const initialize = useCallback(async () => {
    try {
      auth0Client = new Auth0Client({
        ...auth0Config
      });

      // Try checkSession but don't fail if it doesn't work (incognito mode)
      try {
        await auth0Client.checkSession();
      } catch (e) {
        console.log('checkSession failed during init (likely incognito mode):', e.message);
      }

      const isAuthenticated = await auth0Client.isAuthenticated();
      if (isAuthenticated) {
        const user = await auth0Client.getUser();

        // Store user email in sessionStorage for SSO logout scenarios
        if (user?.email) {
          sessionStorage.setItem('userEmail', user.email);
        }

        // Force refresh to verify session is still valid on server
        // This prevents showing 403 when user logged out of OptiFleet but still has cached token
        const {tokenAvailable, accessToken} = await getAccessToken(true)
        if (!tokenAvailable) {
          console.error('Error getting access token:', accessToken);
        }

        //get the user info from the backend endpoint
        const loggedInUser = await GetLoggedInUser(accessToken)

        // Guard against a failed token/user fetch. Throw (rather than return)
        // so the catch below dispatches INITIALISE with isAuthenticated:false —
        // an early return here would skip INITIALISE entirely and hang the app
        // in its uninitialised state.
        if (!loggedInUser?.userInfo?.user?.groups) {
            throw new Error("initialize: no user info returned from GetLoggedInUser");
        }

        const defaultGroup = loggedInUser.userInfo.user.groups.sort((a, b) => a.group.id - b.group.id)[0].group;

        if (!sessionStorage.getItem('groupId')) {
          sessionStorage.setItem('groupId', defaultGroup.id)
          updateUnleashContext({ userId: defaultGroup.id });
        } else {
          const groupId = sessionStorage.getItem('groupId');
          if (!!groupId) {
            updateUnleashContext({ userId: groupId });
          }
        }

        // Set login timestamp for existing session
        lastLoginTimestamp = Date.now();

        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated,
            user: {
              id: user.sub,
              avatar: user.picture,
              email: user.email,
              name: user.name,
              tier: 'Premium'
            },
            loggedInUser: {
              id: loggedInUser.userInfo.user.id,
              emailAddress: loggedInUser.userInfo.user.emailAddress,
              fullName: loggedInUser.userInfo.user.fullName,
              preferredName: loggedInUser.userInfo.user.preferredName,
              adminRole: loggedInUser.userInfo.user.adminRole,
              groupName: defaultGroup.name,
              groups: loggedInUser.userInfo.user.groups,
              isApprover: loggedInUser.userInfo.isApprover,
              createdAt: loggedInUser.userInfo.user.createdAt
            }
          }
        });

        datadogRum.setUser({
          id: loggedInUser.userInfo.user.id.toString(),
          name: loggedInUser.userInfo.user.fullName,
          email: loggedInUser.userInfo.user.emailAddress,
        });
        datadogRum.setGlobalContextProperty('groupName', defaultGroup.name);
      } else {
        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated,
            user: null,
            loggedInUser: null
          }
        });
      }
    } catch (err) {
      console.error(err);
      dispatch({
        type: 'INITIALISE',
        payload: {
          isAuthenticated: false,
          user: null,
          loggedInUser: null
        }
      });
    }

  },[]);

  useEffect(() => {
    initialize()
  },[initialize]);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'Auth0',
        loginWithPopup,
        loginWithRedirect,
        afterLoginRedirection,
        getAccessToken,
        logout,
        refreshLoggedInUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
